import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CardInfo from './CardInfo';
import SkeletonLoader from '../components/SkeletonLoader';
import he from 'he';
import { apiUrl } from '../config/api';
import { normalizeProfileData } from '../lib/profileData';
import { getUserCityLocation } from '../lib/location';
import { resolveScanSourceType } from '../lib/sourceType';
import { useS3ImageUrls } from '../hooks/useS3ImageUrls';

const UserProfile = () => {
  const { cardUrl } = useParams();
  const [searchParams] = useSearchParams();
  const source_type = useMemo(
    () => resolveScanSourceType(searchParams),
    [searchParams]
  );
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { profileImageUrl, coverImageUrl, logoUrl } = useS3ImageUrls(profileData?.cardInfo);

  useEffect(() => {

    if (!cardUrl) {
      setError(true);
      setLoading(false);
      return;
    }

    const recordDigitalCardScan = async (data) => {
      const baseCardScannedInfo = {
        user_id: data.cardInfo.user_id,
        card_id: data.cardInfo.id,
        card_name: data.cardInfo.card_name,
        source_type,
        agent: navigator.userAgent,
      };

      const isMobile = /Mobi|Android/i.test(navigator.userAgent);
      const deviceType = isMobile ? 'Mobile' : 'Desktop';
      const location = await getUserCityLocation({ timeoutMs: 5000 });

      const cardScannedInfo = {
        ...baseCardScannedInfo,
        location,
        device_type: deviceType,
      };

      try {
        await axios.post(apiUrl('/api/digitalCardScan'), cardScannedInfo);
      } catch (error) {
        console.error('Error recording scan:', error);
      }
    };

    const fetchData = async () => {
      try {
        // const response = await axios.get(`https://dashboard.eazylink.co/get_profile.php?business=${userId}`);
        const response = await axios.get(
          apiUrl(`/api/digitalCard/by-url/${encodeURIComponent(cardUrl)}`)
        );
        const data = normalizeProfileData(response.data);

        if (data && data.cardInfo) {
          await recordDigitalCardScan(data);
          setProfileData(data);
          document.title = `UserProfile - ${cardUrl}`;
        }

        // if (data.data.socialmedia.length === 0) {
        //   data.data.socialmedia = [];
        // }

        // data.cardInfo = data.cardInfo.map( digitalCard => ({
        //   ... digitalCard,
        //   name:  digitalCard.card_name,
        //   occupation:  digitalCard.occupation,
        //   description:  digitalCard.description,
        // }));

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(true);
        setLoading(false);
      }
    };

    fetchData();
    updateFavicon();
  }, [cardUrl, source_type]);

  const decodeHtmlEntities = (text) => {
    let decoded = text;
    let previous;
    do {
      previous = decoded;
      decoded = he.decode(decoded);
    } while (decoded !== previous);
    return decoded.replace(/&039;/g, "'");
  };

  const updateFavicon = () => {
    const linkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    linkElement.type = 'image/png';
    linkElement.rel = 'icon';
    linkElement.href = "https://eazylink.co/public/images/logo-icon.png";
    document.head.appendChild(linkElement);
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center bg-gray-100 border border-gray-400 text-gray-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Card not found!</strong>
          <span className="block sm:inline"> contact <a href='https://eazylink.co' className='underline'>support</a></span>
        </div>
      </div>
    );
  }

  return (
    <main className="max-w-2xl mx-auto">
      <CardInfo
        profileData={profileData}
        profileImageUrl={profileImageUrl}
        coverImageUrl={coverImageUrl}
        logoUrl={logoUrl}
        cardUrl={typeof window !== 'undefined' ? window.location.href : ''}
      />
    </main>
  );
};

export default UserProfile;
